import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Button, Grid, useMediaQuery } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import { FC, MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { images } from 'utils/constants/images';
import {
  createDropdownData,
  getDrawerWidth,
  removeErrorFieldsFromValues,
} from 'utils/validators/HelperFunctions';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import AddEdit from '../../../CommonPage/AddEdit';
import { AddEditRoomsForm } from './AddEditRoomsForm';
import { mealConfigForm } from './MealConfigForm';
import PageLoader from 'components/PageLoader';

const AddEditRooms: FC = () => {
  let formRef: RefObject<Form | null | undefined> = useRef();

  const { id } = useParams() as any;
  const theme = useTheme();

  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [pageResponse, setPageResponse] = useState<any>([]);
  const [taxGroups, setTaxGroups] = useState<any>([]);
  const [roomType, setRoomType] = useState<any>([]);
  const [mealConfigResponse, setMealConfigResponse] = useState<any>({});
  const [mealPlanList, setMealPlanList] = useState<any>([]);
  const [mealConfig, setMealConfig] = useState<any>([]);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [isEditDrawer, setIsEditDrawer] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const getInitialResponse = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.getRoomsById(id));
      setPageResponse({ ...data, roomType: data.roomType.value });
      setMealConfig(data.mealPlanConfigs.map((item: any) => ({ ...item, name: item.mealPlan.value })));
    } catch (error: any) {
      console.error('Error fetching room details', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
      }
    })();
  }, [getInitialResponse, id]);

  const pageConfig = {
    title: id ? strings.room_title_edit : strings.room_title_add,
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: id ? URLS.getRoomsById(id) : URLS.addRooms,
    initialApi: URLS.getRoomsById(id),
    formModel: AddEditRoomsForm(strings, roomType),
    navigateToPage: routes.rooms,
    isCustomResponse: false,
  };

  const getTaxGroups = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.taxGroupDropdown);
      setTaxGroups(data);
    } catch (error: any) {
      console.error('Error fetching tax groups', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getMealPlansDropdown = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.getMealPlansDropdown);
      const filter = data.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      setMealPlanList(filter);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  }, []);

  const getRoomType = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.roomTypeDropdown);
      const filter = data?.map((item: any) => {
        return { label: item.name, value: item.id };
      });
      setRoomType(filter);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getTaxGroups();
    getRoomType();
    getMealPlansDropdown();
  }, [getRoomType, getTaxGroups, getMealPlansDropdown]);

  const handleCustomResponse = (custom: any) => {
    return {
      ...custom,
      roomType: custom.roomType,
      serviceChargePercentage: custom.serviceChargePercentage || 0,
      taxGroupId: custom.taxGroup?.value ?? null,
      mealPlans: mealConfig.map((item: any) => ({
        id: item.id,
        mealPlanId: item.name,
        price: item.price || 0,
      })),
    };
  };

  const mealConfigTable = () => {
    const columns = [
      { field: 'name', headerName: strings.meal_plan_column_name, flex: 1, minWidth: 150 },
      { field: 'price', headerName: strings.meal_plan_price_column_name, flex: 1, minWidth: 150 },
      {
        field: 'Action',
        headerName: strings.customermgmt_column_action,
        sortable: false,
        flex: 1,
        minWidth: 150,
        renderCell: (ValueFormatterParams: any) => {
          return (
            <Stack direction="row" spacing={2}>
              <img
                src={images.editIcon}
                alt="edit"
                width="20px"
                height="20px"
                style={{ cursor: 'pointer' }}
                onClick={() => editRow(ValueFormatterParams.row.id)}
              />
              <img
                className={'cursor-pointer'}
                src={images.deleteIcon}
                alt="delete"
                width="20px"
                height="20px"
                style={{ cursor: 'pointer' }}
                onClick={() => deleteRow(ValueFormatterParams.row.id)}
              />
            </Stack>
          );
        },
      },
    ];
    const value =
      mealConfig?.length > 0 ? (
        <div style={{ width: '100%' }}>
          <div style={{ height: 300, width: '100%' }}>
            <DataGrid
              sx={{
                overflow: 'auto',
                '.MuiDataGrid-row': {
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: `${theme.palette.primary.main}09}`,
                  },
                },
                '.MuiDataGrid-columnHeaders': {
                  backgroundColor: `${theme.palette.primary.main}35}`,
                },
              }}
              rowHeight={60}
              rows={mealConfig.map((item: any, idx: any) => ({
                ...item,
                name: mealPlanList.find((i: any) => i.value === item.name)?.label,
                value: item.name,
                id: idx,
              }))}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
            />
          </div>
        </div>
      ) : null;
    return value;
  };

  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(mealConfig));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);
    setMealConfig(deleteValue);
  };

  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editRow = JSON.parse(JSON.stringify(mealConfig));
    const editValue = editRow.find((item: any, i: any) => i === idx);
    setMealConfigResponse({ ...editValue });
    setIsEditDrawer(true);
    setOpenDrawer(true);
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setMealConfigResponse({});
    formRef.current?.resetForm();
    setIsEditDrawer(false);
    setSelectedRowId(null);
    setHasError(false);
  };

  const mealPlanConfigFormComponent = () => {
    return (
      <>
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Form
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={mealConfigForm(strings, mealPlanList)}
            values={mealConfigResponse}
          />
        </Grid>
        <Grid item sx={{ my: 1 }} xs={12} sm={3} lg={2}>
          <Button variant="contained" sx={{ color: '#ffffff' }} onClick={e => handleSave(e)} fullWidth>
            {strings.common_button_save}
          </Button>
        </Grid>
      </>
    );
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData);
    setHasError(false);
    if (isFormValid) {
      const array = [...mealConfig];
      if (selectedRowId !== null) {
        array[selectedRowId] = { ...array[selectedRowId], ...data };
        setSelectedRowId(null);
      } else {
        array.push(data);
      }
      setMealConfig(array);
      setOpenDrawer(false);
      setMealConfigResponse({});
      setIsEditDrawer(false);
    } else {
      setHasError(true);
    }
  };

  return (
    <>
      {loading && <PageLoader />}
      <AddEdit
        handleCustomResponse={handleCustomResponse}
        dropdownValue={{ taxGroup: createDropdownData(taxGroups, ['id', 'name']) }}
        pageConfig={pageConfig}
        pageResponse={pageResponse}>
        <Button variant="text" onClick={() => setOpenDrawer(true)}>
          <span style={{ marginRight: '10px' }}>{strings.addRoomMealConfigBudget}</span>
          <AddCircleOutlineIcon />
        </Button>
        {mealConfigTable()}
        <DrawerMenu
          width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
          open={openDrawer}
          handleClose={handleClose}
          closeButton={true}
          title={isEditDrawer ? strings.editRoomMealConfigBudget : strings.addRoomMealConfigBudget}>
          {mealPlanConfigFormComponent()}
        </DrawerMenu>
      </AddEdit>
    </>
  );
};

export default AddEditRooms;
